import {Stack, Alert} from '@mui/material'
import React, {useState} from 'react'
import MainBtn from '../../customTable/MainBtn'
import PopUp from '../../customTable/PopUp'
import PackagesApi from '../../../Api/Packages'
import Snackbar, {SnackbarCloseReason} from '@mui/material/Snackbar'
import {useTranslation} from 'react-i18next'
import {useSelector} from 'react-redux'
import {RootState} from '../../../store'
import PaymentGatewaySelector from '../../wallet/components/PaymentGatewaySelector'
import OTPModal from '../../wallet/components/OTPModal'
import {
  handlePaymentInitiationResponse,
  parsePaymentAmount,
  formatPaymentDisplay,
  resolveLocalizedString,
  isBlomUnifiedCheckout,
  runBlomUnifiedCheckout,
  toIsoCurrencyCode,
} from '../../../utils/paymentHelpers'

type FlowStep = 'otp' | 'gateway'

export default function RowBtn({
  index,
  prices,
  value,
  duration,
  isRenew,
  name,
  type,
  i,
  text,
  myKey,
  setNum,
  disabled,
}: any) {
  const {t} = useTranslation()
  const [open, setOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isResending, setIsResending] = useState(false)
  const [flowStep, setFlowStep] = useState<FlowStep>('otp')
  const [signature, setSignature] = useState('')
  const [otpAmountLabel, setOtpAmountLabel] = useState('')
  const [errorMsg, setErrorMsg] = useState('')
  const [openAlert, setOpenAlert] = React.useState(false)

  const {lang, defLang} = useSelector((state: RootState) => state.vender)
  const countryId = Number(localStorage.getItem('country_id') || 0)
  const currency = (localStorage.getItem('currency') || 'USD').trim()
  const amount = parsePaymentAmount(prices?.[myKey])
  const planName =
    resolveLocalizedString(name?.[myKey], lang, defLang) ||
    resolveLocalizedString(name, lang, defLang) ||
    ''
  const durationLabel =
    resolveLocalizedString(duration?.[myKey], lang, defLang) ||
    (duration?.[myKey] != null ? String(duration[myKey]) : '')
  const startDateLabel = new Date().toLocaleDateString()
  const priceLabel = formatPaymentDisplay(prices?.[myKey] ?? amount, currency)
  const planType = type[myKey]

  const handleClose = (_event?: React.SyntheticEvent | Event, reason?: SnackbarCloseReason) => {
    if (reason === 'clickaway') {
      return
    }
    setOpenAlert(false)
  }

  const resetFlow = () => {
    setFlowStep('otp')
    setSignature('')
    setErrorMsg('')
    setOtpAmountLabel('')
  }

  const closeFlow = () => {
    if (isLoading) {
      return
    }
    setOpen(false)
    resetFlow()
  }

  const startCheckout = () => {
    setErrorMsg('')
    if (isRenew) {
      PackagesApi.renew((res: any) => {
        const nextSignature = res?.signature ?? res?.data?.signature
        const nextAmount = parsePaymentAmount(res?.amount ?? res?.data?.amount ?? amount)
        const nextCurrency = String(res?.currency ?? res?.data?.currency ?? currency).trim()
        if (!nextSignature) {
          setErrorMsg(String(t('paymentGatewaysLoadError') || 'Unable to start verification'))
          return
        }
        setSignature(String(nextSignature))
        setOtpAmountLabel(formatPaymentDisplay(nextAmount, nextCurrency))
        setFlowStep('otp')
        setOpen(true)
      }, setIsLoading)
      return
    }

    PackagesApi.upgrade(
      {plan_id: index, plan_type: planType},
      (res: any) => {
        const nextSignature = res?.signature ?? res?.data?.signature
        const nextAmount = parsePaymentAmount(res?.amount ?? res?.data?.amount ?? amount)
        const nextCurrency = String(res?.currency ?? res?.data?.currency ?? currency).trim()
        if (!nextSignature) {
          setErrorMsg(String(t('paymentGatewaysLoadError') || 'Unable to start verification'))
          return
        }
        setSignature(String(nextSignature))
        setOtpAmountLabel(formatPaymentDisplay(nextAmount, nextCurrency))
        setFlowStep('otp')
        setOpen(true)
      },
      setIsLoading
    )
  }

  const handleOtpSubmit = (code: string) => {
    setErrorMsg('')
    if (isRenew) {
      PackagesApi.renewConfirm({signature, code}, () => setFlowStep('gateway'), setIsLoading)
      return
    }
    PackagesApi.upgradeConfirm(
      {signature, code, plan_id: index, plan_type: planType},
      () => setFlowStep('gateway'),
      setIsLoading
    )
  }

  const handleGatewaySelected = (factoryName: string) => {
    const currency_code = toIsoCurrencyCode(currency)
    // UI "downgrade" still uses subscription pay with process=upgrade (plan change OTP scope).
    const payload = isRenew
      ? {process: 'renew' as const, currency_code}
      : {
          process: 'upgrade' as const,
          plan_id: index,
          plan_type: planType,
          currency_code,
        }

    if (isBlomUnifiedCheckout(factoryName)) {
      runBlomUnifiedCheckout(
        {
          purpose: 'subscription',
          ...payload,
        },
        setIsLoading
      )
      return
    }

    PackagesApi.payByGateway(
      factoryName,
      payload,
      (res: any) => {
        handlePaymentInitiationResponse(res)
      },
      setIsLoading
    )
  }

  return (
    <Stack>
      <Snackbar open={openAlert} autoHideDuration={6000} onClose={handleClose}>
        <Alert onClose={handleClose} severity='info' variant='filled' sx={{width: '100%'}}>
          {t('youAreSubscribedToThisPackage')}
        </Alert>
      </Snackbar>
      <MainBtn
        disabled={disabled}
        handelSubmit={() => {
          if (text === 'currentPlan') {
            setOpenAlert(true)
          } else {
            startCheckout()
          }
        }}
        br='8px'
        minWidth='101px'
        bgcolor={value}
        text={text}
      />

      <PopUp isLoading={isLoading} width='sm' open={open} setOpen={setOpen}>
        <Stack p={{xs: '16px', sm: '24px'}} gap='12px' bgcolor='#fff'>
          {errorMsg ? <Alert severity='error'>{errorMsg}</Alert> : null}
          {flowStep === 'otp' ? (
            <OTPModal
              signature={signature}
              amountLabel={otpAmountLabel || priceLabel}
              amount={amount}
              currency={currency}
              planName={planName}
              isLoading={isLoading}
              isResending={isResending}
              onSubmit={handleOtpSubmit}
              onCancel={closeFlow}
              onResend={() => PackagesApi.resendOtp({signature}, () => {}, setIsResending)}
            />
          ) : (
            <PaymentGatewaySelector
              open={open && flowStep === 'gateway'}
              onClose={closeFlow}
              amount={amount}
              currency={currency}
              purpose='subscription'
              countryId={countryId}
              referenceId={isRenew ? 0 : Number(index) || 0}
              isPaying={isLoading}
              onGatewaySelected={handleGatewaySelected}
              summary={{
                titleKey: 'completePayment',
                planName,
                priceLabel,
                durationLabel,
                startDateLabel,
              }}
            />
          )}
        </Stack>
      </PopUp>
    </Stack>
  )
}
